import { DRUM_LANES, resizeDrumPattern, type DrumLane } from "../../sequencer/patterns";
import { STEP_TICKS, barTicks, stepsPerBar } from "../../sequencer/time";
import { usePosition, useProject, useServices } from "../context";
import { Panel } from "./controls";
import { SampledControls } from "./SampledControls";
import { SoundPicker } from "./SoundPicker";

const LABEL: Record<DrumLane, string> = { kick: "Kick", snare: "Snare", hat: "Hi-hat", clap: "Clap" };

export function StepGrid() {
  const { controller } = useServices();
  const drums = useProject((p) => p.sequence.drums);
  const ts = useProject((p) => p.song.timeSignature);
  const pos = usePosition();
  const steps = stepsPerBar(ts);
  // The drum bar repeats inside the loop; show the step within the current bar.
  const current = pos === null ? -1 : Math.floor((pos % barTicks(ts)) / STEP_TICKS);
  const pattern = drums.pattern.steps === steps ? drums.pattern : resizeDrumPattern(drums.pattern, steps);

  const toggle = (lane: DrumLane, i: number) => controller.updateProject((p) => {
    const pat = resizeDrumPattern(p.sequence.drums.pattern, steps);
    const row = [...pat.lanes[lane]];
    row[i] = !row[i];
    return { ...p, sequence: { ...p.sequence, drums: { ...p.sequence.drums, pattern: { ...pat, lanes: { ...pat.lanes, [lane]: row } } } } };
  });
  const clear = () => controller.updateProject((p) => ({ ...p, sequence: { ...p.sequence, drums: { ...p.sequence.drums, pattern: resizeDrumPattern({ steps: 0, lanes: { kick: [], snare: [], hat: [], clap: [] } }, steps) } } }));

  return (
    <Panel title="Drum pattern" className="span-all" actions={<button type="button" onClick={clear}>Clear</button>}>
      <SoundPicker trackId={drums.trackId} drums />
      <SampledControls trackId={drums.trackId} />
      <div className="step-grid" role="grid" aria-label={`Drum steps, ${steps} sixteenth notes per bar`} style={{ ["--steps" as string]: steps }}>
        {DRUM_LANES.map((lane) => (
          <div className="step-row" role="row" key={lane}>
            <span className="step-lane" role="rowheader">{LABEL[lane]}</span>
            {Array.from({ length: steps }, (_, i) => {
              const on = pattern.lanes[lane][i] ?? false;
              return (
                <button
                  key={i} type="button" role="gridcell" aria-pressed={on}
                  aria-label={`${LABEL[lane]} step ${i + 1}${on ? " on" : " off"}`}
                  className={`step ${on ? "on" : ""} ${i % 4 === 0 ? "beat" : ""} ${i === current ? "playing" : ""}`}
                  onClick={() => toggle(lane, i)}
                >
                  <span aria-hidden="true">{on ? "●" : ""}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </Panel>
  );
}
